import { useState, useEffect } from 'react';
import { accountabilityAPI } from '../lib/api';
import { Shield, Gavel, Eye, EyeOff, AlertTriangle, Loader2, CheckCircle2, XCircle, User } from 'lucide-react';

function ShadowModeToggle() {
  const [loading, setLoading] = useState(true);
  const [shadow, setShadow] = useState(true);
  const [flagCount, setFlagCount] = useState(0);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    accountabilityAPI
      .getModSettings()
      .then((r) => {
        setShadow(r.data.shadow_mode);
        setFlagCount(r.data.total_flags || 0);
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const toggle = async () => {
    setSaving(true);
    try {
      const newVal = !shadow;
      await accountabilityAPI.setShadowMode(newVal);
      setShadow(newVal);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="font-hud text-xs text-[#52525B] animate-pulse">LOADING…</div>;

  return (
    <div className="border border-[#2F8A92]/20 rounded p-3 space-y-2" data-testid="shadow-mode-toggle">
      <div className="flex items-center justify-between">
        <div>
          <h5 className="font-hud text-xs tracking-widest text-[#2F8A92] uppercase flex items-center gap-1">
            {shadow ? <EyeOff size={11} /> : <Eye size={11} />} AI MODERATION
          </h5>
          <p className="font-mono text-[10px] text-white/80 mt-0.5">
            {shadow ? 'Shadow mode — flags logged, strikes NOT fired.' : 'Live — high-severity flags auto-issue strikes.'}
          </p>
          <p className="font-mono text-[9px] text-[#52525B] mt-0.5">
            Total flags logged: {flagCount}
          </p>
        </div>
        <button
          data-testid="shadow-toggle-btn"
          onClick={toggle}
          disabled={saving}
          className={`font-hud text-[10px] tracking-wider px-3 py-1.5 border transition-all ${
            shadow
              ? 'border-[#D4A84A]/40 text-[#D4A84A] hover:bg-[#D4A84A]/10'
              : 'border-[#FF4444]/40 text-[#FF4444] hover:bg-[#FF4444]/10'
          }`}
        >
          {saving ? <Loader2 size={10} className="animate-spin" /> : shadow ? 'GO LIVE' : 'SHADOW MODE'}
        </button>
      </div>
    </div>
  );
}

function AppealVoteForm({ appeal, onVoted }) {
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const vote = async (decision) => {
    setSubmitting(true);
    try {
      await accountabilityAPI.voteAppeal(appeal.id, decision, notes.trim());
      onVoted?.();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-2 space-y-2">
      <textarea
        data-testid={`vote-notes-${appeal.id}`}
        className="hud-input text-xs min-h-[60px] resize-y"
        placeholder="Your reasoning (audit-logged)"
        value={notes}
        onChange={(e) => setNotes(e.target.value.slice(0, 1000))}
      />
      <div className="flex gap-2">
        <button
          data-testid={`vote-expunge-${appeal.id}`}
          onClick={() => vote('expunge')}
          disabled={submitting}
          className="flex-1 font-hud text-[10px] tracking-wider text-[#0A0A0A] bg-[#2F8A92] px-3 py-1.5 hover:bg-[#66FAFF] disabled:opacity-40 flex items-center justify-center gap-1"
        >
          <CheckCircle2 size={10} /> EXPUNGE
        </button>
        <button
          data-testid={`vote-uphold-${appeal.id}`}
          onClick={() => vote('uphold')}
          disabled={submitting}
          className="flex-1 font-hud text-[10px] tracking-wider text-red-400 border border-red-400/40 px-3 py-1.5 hover:bg-red-400/10 disabled:opacity-40 flex items-center justify-center gap-1"
        >
          <XCircle size={10} /> UPHOLD
        </button>
      </div>
    </div>
  );
}

function AppealQueue() {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const load = () => {
    setLoading(true);
    accountabilityAPI.appealQueue().then((r) => setQueue(r.data || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="font-hud text-xs text-[#52525B] animate-pulse">LOADING QUEUE…</div>;

  return (
    <div className="space-y-2" data-testid="appeal-queue">
      <h5 className="font-hud text-xs tracking-widest text-[#2F8A92] uppercase flex items-center gap-1">
        <Gavel size={11} /> APPEAL QUEUE ({queue.length})
      </h5>
      {queue.length === 0 ? (
        <p className="font-mono text-[10px] text-[#52525B]">Queue empty.</p>
      ) : (
        queue.map((a) => (
          <div key={a.id} className="border border-[#2F8A92]/15 rounded p-3" data-testid={`queue-appeal-${a.id}`}>
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="font-hud text-[10px] tracking-wider text-[#D4A84A] uppercase">
                  STRIKE {a.strike_level} · {a.route.replace('_', ' ')}
                </p>
                <p className="font-mono text-[9px] text-[#52525B]">
                  User {a.user_id.slice(-6)} · filed {new Date(a.created_at).toLocaleString()}
                </p>
              </div>
              <button
                onClick={() => setExpandedId(expandedId === a.id ? null : a.id)}
                className="font-hud text-[10px] tracking-wider text-[#2F8A92] hover:text-[#66FAFF]"
              >
                {expandedId === a.id ? 'HIDE' : 'REVIEW'}
              </button>
            </div>
            {expandedId === a.id && (
              <div className="mt-2 space-y-2">
                <div className="border-l-2 border-[#2F8A92]/30 pl-2">
                  <p className="font-mono text-[10px] text-white/80">{a.statement}</p>
                </div>
                <p className="font-mono text-[9px] text-[#52525B]">Votes so far: {a.votes?.length || 0}</p>
                <AppealVoteForm appeal={a} onVoted={load} />
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

function ManualStrikeForm() {
  const [userId, setUserId] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handle = async () => {
    setSubmitting(true); setError('');
    try {
      const r = await accountabilityAPI.manualStrike(userId.trim(), reason.trim());
      setResult(r.data);
      setUserId(''); setReason('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="border border-[#D4A84A]/20 rounded p-3 space-y-2" data-testid="manual-strike-form">
      <h5 className="font-hud text-xs tracking-widest text-[#D4A84A] uppercase flex items-center gap-1">
        <User size={11} /> ISSUE MANUAL STRIKE
      </h5>
      <input
        data-testid="manual-strike-user-id"
        className="hud-input text-xs"
        placeholder="User ID (ObjectId from MongoDB)"
        value={userId}
        onChange={(e) => setUserId(e.target.value)}
      />
      <textarea
        data-testid="manual-strike-reason"
        className="hud-input text-xs min-h-[60px] resize-y"
        placeholder="Reason — will appear in the user's record and audit trail."
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      {error && <p className="font-mono text-[10px] text-red-400">{error}</p>}
      {result && (
        <p className="font-mono text-[10px] text-[#2F8A92]">
          Strike {result.strike_level} issued for user · {new Date(result.issued_at).toLocaleString()}
        </p>
      )}
      <button
        data-testid="manual-strike-btn"
        onClick={handle}
        disabled={submitting || !userId.trim() || !reason.trim()}
        className="font-hud text-[10px] tracking-wider text-[#0A0A0A] bg-[#D4A84A] px-3 py-1.5 hover:bg-[#D4A84A]/80 disabled:opacity-40 flex items-center gap-1"
      >
        {submitting ? <Loader2 size={10} className="animate-spin" /> : <AlertTriangle size={10} />}
        ISSUE STRIKE
      </button>
    </div>
  );
}

export default function AccountabilityDashboard() {
  return (
    <div className="space-y-4" data-testid="accountability-dashboard">
      <h3 className="font-hud text-[#2F8A92] text-sm tracking-widest uppercase flex items-center gap-2">
        <Shield size={14} /> ACCOUNTABILITY
      </h3>
      <ShadowModeToggle />
      <AppealQueue />
      <ManualStrikeForm />
    </div>
  );
}
